import { useState } from 'react';
import { CSVLink } from 'react-csv';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { NodeAccessApproval, NodeAccessOperation, NodeAccessProfileStatus } from '@prisma/client';
import { Button, ButtonProps, Layout, Modal, Select, Space, Table } from 'antd';
import { ColumnType } from 'antd/es/table';

import { formatIsoDate } from '@app/utils-agnostic';
import { PageLayout, Tables } from '@app/web-components';

import { NodeSupplierContracts } from '../../admin';
import { ProfileOperationRenderer, ProfileStatusRenderer } from '../../profiles';
import { useBulkApprove, useFindApprovals } from '../data';
import { useApprovalActions } from '../libs';

import { LandingPageItem } from './landing';

export const ExternalLandingPage = () => {
  const { getApprovalModalProps, getActionButton } = useApprovalActions();
  const [searchParams, setSearchParams] = useSearchParams({});
  const [selectedContract, setSelectedContract] = useState<string | undefined>(
    searchParams.get('contractNumber') ?? undefined,
  );
  const [selectedApprovals, setSelectedApprovals] = useState<number[]>([]);

  const { getSelectProps: getContractSelectProps } = NodeSupplierContracts.useContractPicker({
    labelInValue: false,
    value: selectedContract,
  });

  const dataColumns: (ColumnType<LandingPageItem> & { csvFormatter?: (value: unknown) => string })[] = [
    {
      title: 'Operation',
      dataIndex: 'operation',
      width: 100,
      render: (operation) => <ProfileOperationRenderer operation={operation} />,
      filters: [NodeAccessOperation.ADD, NodeAccessOperation.DELETE].map((status) => ({
        text: status,
        value: status,
      })),
      filterSearch: true,
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (status) => <ProfileStatusRenderer status={status} />,
      filters: Object.keys(NodeAccessProfileStatus).map((status) => ({
        text: status,
        value: status,
      })),
      filterSearch: true,
      sorter: true,
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Node',
      dataIndex: 'nodeName',
      width: 80,
      sorter: true,
      // Filterdropdown instead of filters here because this is an external page and users don't need to know all the nodes present.
      filterDropdown: Tables.customSearchFilterDropdownFactory('Search nodes'),
    },
    {
      title: 'Contract Name',
      dataIndex: 'contractName',
      width: 150,
      sorter: true,
      ellipsis: true,
    },
    {
      title: 'CW Name',
      dataIndex: 'name',
      width: 150,
      sorter: true,
      filterDropdown: Tables.customSearchFilterDropdownFactory('Search users'),
    },
    {
      title: 'CW Job Title',
      dataIndex: 'jobRole',
      width: 150,
      sorter: true,
      ellipsis: true,
      filterDropdown: Tables.customSearchFilterDropdownFactory('Search job roles'),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 150,
      sorter: true,
      ellipsis: true,
    },
    {
      title: 'Project',
      dataIndex: 'dieName',
      width: 150,
      sorter: true,
      filterDropdown: Tables.customSearchFilterDropdownFactory('Search projects'),
    },
    {
      title: 'Contract Number',
      dataIndex: 'contractNumber',
      width: 100,
      sorter: true,
      // Without this excel will treat the contract number as a number and remove leading zeros and converts to scientific notation.
      csvFormatter: (value: string) => (value ? `\t${value}` : ''),
    },
    {
      title: 'Signed Date',
      dataIndex: 'intelSignedDate',
      width: 120,
      sorter: true,
      render: (date) => formatIsoDate(date, false),
      csvFormatter: (date: Date) => formatIsoDate(date, false),
    },
  ];

  const {
    getParentLayoutContentProps,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getTableProps,
    getToolbarProps,
    getQuery,
  } = Tables.useTableState<LandingPageItem>(
    'Approvals',
    [
      {
        title: 'Action',
        width: 120,
        render: (_, record) => getActionButton(record),
      },
      ...dataColumns,
    ],
    {
      globalSearchFields: ['name', 'dieName', 'nodeName', 'contractName'],
    },
  );

  const queryResult = useFindApprovals<LandingPageItem>(
    getQuery({
      where: {
        contractNumber: selectedContract ?? undefined,
      },
    }),
    { refetchInterval: 60000 },
  );

  const contractApprovals = useFindApprovals(
    {
      ...getQuery({
        where: {
          contractNumber: selectedContract ?? undefined,
        },
      }),
      skip: undefined,
      take: undefined,
    },
    { refetchInterval: 60000 },
  );

  const bulkApprove = useBulkApprove({
    onSettled: (data, error) => {
      if (error) {
        console.log(error);
      } else {
        queryResult.refetch();
        setSelectedApprovals([]);
      }
    },
  });

  const getApproveSelectedButtonProps = (): ButtonProps => ({
    children: 'Approve Selected',
    icon: <CheckCircleOutlined />,
    disabled: !selectedApprovals.length,
    onClick: () => bulkApprove.mutate({ approvalIds: selectedApprovals }),
    loading: bulkApprove.isPending,
  });

  const getCSVLinkProps = () => {
    const props = {
      data:
        contractApprovals?.data?.data?.map((approval) => {
          const row = [];
          dataColumns.forEach((column) => {
            const dataIndex = column.dataIndex as keyof LandingPageItem;
            const value = column.csvFormatter ? column.csvFormatter(approval[dataIndex]) : approval[dataIndex];
            row.push(value);
          });
          return row;
        }) ?? [],
      headers: dataColumns.map((column) => column.title as string),
      filename: `${selectedContract ?? 'approvals'}.csv`,
      children: (
        <Button icon={<DownloadOutlined />} loading={contractApprovals?.isLoading}>
          Export to CSV
        </Button>
      ),
    };
    return props;
  };

  return (
    <>
      <Modal {...getApprovalModalProps()} />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Approvals" />
        <Space direction="horizontal">
          <div
            {...getToolbarProps({
              items: [
                <Select
                  {...getContractSelectProps({
                    allowClear: true,
                    placeholder: 'Search Contracts',
                    onChange: (newValue) => {
                      setSelectedContract(newValue);
                      if (newValue) setSearchParams({ contractNumber: newValue });
                      else setSearchParams({});
                    },
                  })}
                  value={selectedContract}
                />,
                <Button {...getApproveSelectedButtonProps()} />,
                <Button
                  {...getReloadButtonProps()}
                  onClick={() => {
                    queryResult?.refetch();
                    if (selectedContract) contractApprovals?.refetch();
                  }}
                />,
                <Button {...getResetFiltersButtonProps()} />,
                <CSVLink {...getCSVLinkProps()} />,
              ],
              queryResult,
            })}
          />
        </Space>
        <Table
          {...getTableProps(queryResult)}
          rowSelection={{
            selectedRowKeys: selectedApprovals,
            getCheckboxProps: (record: NodeAccessApproval) => ({
              disabled: !(record.status === NodeAccessProfileStatus.PENDING),
            }),
            onChange: (selectedRowKeys) => setSelectedApprovals(selectedRowKeys as number[]),
            columnWidth: 48,
          }}
          rowKey={(record) => record.id}
        />
      </Layout.Content>
    </>
  );
};
